/**
 * Add Parts modal for the Expense Inventory Sampling tab, modeled after
 * InventoryReturnDetail's InventoryAddParts. Reuses the shared Usage/Parts
 * hooks and components for searching Repstock/Catalog inventory and
 * tracking selected quantities. Customer inventory is not a valid source for
 * Expense Inventory, so it is always skipped. Repstock is the rep's IT
 * accounts, plus IH accounts when an Ops user is viewing from the review
 * queue.
 * @module src/components/Inventory/expense/detail/ExpenseInventorySampling/AddPartsModal/ExpenseInventoryAddParts
 */
import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Modal from '@salesforce/design-system-react/components/modal';
import Spinner from '@salesforce/design-system-react/components/spinner';
import Button from '@salesforce/design-system-react/components/button';
import { getUserInfo } from '../../../../../../store/ducks/userInfo';
import useSnackBar from '../../../../../../util/customHooks/useSnackBar';
import SnackBar from '../../../../../Shared/SnackBar';
import CartComponent from '../../../../../Usage/UsageDetails/UsageTabs/Parts/CartComponent';
import SearchBar from '../../../../../Usage/UsageDetails/UsageTabs/Parts/AddParts/SearchBar/SearchBar';
import InventorySection from '../../../../../Usage/UsageDetails/UsageTabs/Parts/AddParts/InventorySection/InventorySection';
import useParts, {
  INVENTORY_CHANNELS,
} from '../../../../../Usage/UsageDetails/UsageTabs/Parts/AddParts/useParts';
import { ExpenseInventoryAddPartsProps } from './ExpenseInventoryAddParts.types';
import useSaveExpenseInventoryParts from './hook/useSaveExpenseInventoryParts';
import { toAddPartsLineItems } from '../samplingLineRules';
import styles from './styles.module.scss';
import { USAGE_TYPE } from '../../../../../../util/constants';

const ExpenseInventoryAddParts: FC<ExpenseInventoryAddPartsProps> = ({
  isOpen,
  isReview = false,
  handleClose,
  expenseInventoryDetail,
  count = 0,
  onPartsAdded,
}) => {
  // #region Hooks
  const userInfo = useSelector(getUserInfo);
  const { open, notification, openSnackBar } = useSnackBar();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Expense Inventory parts may only be added from Repstock or Catalog, never Customer.
  const partsUsageDetails = useMemo(
    () => ({
      ...expenseInventoryDetail,
      accountNumber: expenseInventoryDetail?.subInventoryCode,
      caseId: expenseInventoryDetail?.id,
      isReview: !!isReview,
      lineItems: toAddPartsLineItems(expenseInventoryDetail?.lineItems),
    }),
    [expenseInventoryDetail, isReview]
  );

  const {
    searchText,
    searchInventory,
    inventoryCount,
    repstockInventory,
    loadingRepstockInventory,
    handleInventoryCount,
    productGroups,
    loadingProductGroups,
    selectedGroup,
    setSelectedGroup,
    loadingCatalogInventory,
    catalogInventory,
    handleCatalogCount,
    inventoryPartsToAdd,
    catalogPartsToAdd,
  } = useParts({
    usageDetails: partsUsageDetails,
    shouldLoadCatalogByDefault: false,
    shouldSkipCustomer: true,
    shouldSkipRepstock: false,
    initialPartsCount: count,
    recordType: USAGE_TYPE.EXPENSE_INVENTORY,
    userInfo,
    openSnackBar,
  });

  const { onAddClick, loadingLineItems } = useSaveExpenseInventoryParts({
    setIsSaving,
    expenseInventoryDetail,
    inventoryPartsToAdd,
    catalogPartsToAdd,
    handleClose,
    openSnackBar,
    onPartsAdded,
  });
  // #endregion Hooks

  return (
    <IconSettings iconPath="/icons">
      <SnackBar open={open} notification={notification} />
      <Modal
        ariaHideApp={false}
        isOpen={isOpen}
        footer={[
          <div className="usage-modal-footer">
            {(isSaving || loadingRepstockInventory || loadingCatalogInventory) && (
              <Spinner size="small" />
            )}
            <Button
              label="Cancel"
              onClick={() => {
                handleClose();
              }}
            />
            <Button label="Add" variant="brand" onClick={onAddClick} />
          </div>,
        ]}
        onRequestClose={() => {
          handleClose();
        }}
        dismissOnClickOutside={false}
      >
        <div className={styles.cartComponentContainer}>
          <p className={styles.title}>Add Parts</p>
          <div className={styles.cartComponentAlign}>
            <CartComponent count={inventoryCount} color="black" textColor="white" />
          </div>
        </div>
        <div className={styles.modalInnerContainer}>
          {loadingLineItems && <Spinner size="large" />}
          <SearchBar
            searchInventory={searchInventory}
            loading={loadingRepstockInventory}
            productGroups={productGroups}
            loadingProductGroups={loadingProductGroups}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
          />
          <div className={styles.sectionsContainer}>
            <InventorySection
              title="Repstock"
              searching={searchText.length > 2}
              inventoryType={INVENTORY_CHANNELS.REPSTOCK}
              inventory={repstockInventory}
              loading={loadingRepstockInventory}
              handleInventoryCounter={handleInventoryCount}
            />
            <InventorySection
              title="Catalog"
              searching={searchText.length > 2}
              inventoryType={INVENTORY_CHANNELS.CATALOG}
              inventory={catalogInventory}
              loading={loadingCatalogInventory}
              handleCatalogCounter={handleCatalogCount}
              isInventoryReturn
            />
          </div>
        </div>
      </Modal>
    </IconSettings>
  );
};

export default ExpenseInventoryAddParts;
